import { useCallback } from 'react';
import type { LoginInput, ChildLoginInput, ChangePasswordInput } from '@kidshome/api-contract';
import { useAuthStore } from '@/entities/session/auth-store';
import * as authApi from '@/entities/session/auth-api';
import { markLoggedOut, clearLoggedOutMarker } from '@/entities/session/bootstrap';
import { DRAFT_PREFIX } from '@/shared/lib/useDraftAutosave';

/**
 * Session actions used by the auth forms. Each updates the in-memory store and ensures a CSRF token
 * is available for subsequent cookie-route calls (logout/change-password/refresh). All HTTP goes
 * through the generated client.
 */
export function useAuthActions(): {
  login: (input: LoginInput) => Promise<void>;
  childLogin: (input: ChildLoginInput) => Promise<void>;
  changePassword: (input: ChangePasswordInput) => Promise<void>;
  logout: () => Promise<void>;
} {
  const setSession = useAuthStore((s) => s.setSession);
  const setCsrfToken = useAuthStore((s) => s.setCsrfToken);
  const clear = useAuthStore((s) => s.clear);

  const ensureCsrf = useCallback(async () => {
    const { csrfToken } = await authApi.fetchCsrfToken();
    setCsrfToken(csrfToken);
  }, [setCsrfToken]);

  const login = useCallback(
    async (input: LoginInput) => {
      const session = await authApi.login(input);
      setSession(session);
      clearLoggedOutMarker();
      await ensureCsrf();
    },
    [setSession, ensureCsrf],
  );

  const childLogin = useCallback(
    async (input: ChildLoginInput) => {
      const session = await authApi.childLogin(input);
      setSession(session);
      clearLoggedOutMarker();
      await ensureCsrf();
    },
    [setSession, ensureCsrf],
  );

  const changePassword = useCallback(
    async (input: ChangePasswordInput) => {
      // The change-password route is CSRF-protected (cookie session); make sure a token is present
      // (it normally is from login/bootstrap, but refresh defensively so a long-idle tab still works).
      await ensureCsrf();
      await authApi.changePassword(input);
    },
    [ensureCsrf],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clear();
      // Poison silent refresh-restore until the next interactive login: if the logout POST failed to reach
      // the server, the refresh cookie is still valid and a cold reload would otherwise auto-restore this
      // guardian on a shared tablet. Set BEFORE navigation so the next probeSession sees it.
      markLoggedOut();
      // Deliberately keep this device's cached "Who's playing?" roster (known-kids.ts) — this is the
      // shared-family-tablet handoff: a guardian logs out and the kid picker must show their kids
      // immediately, with no second login. `useRememberKids` already overwrites the cache wholesale
      // the moment any guardian is authenticated, so a genuinely different household signing in on
      // this device replaces the stale roster before its own kids could ever reach the picker.
      // Purge every form-draft autosave (ADR-0033) so one guardian's in-progress edit can't prefill
      // another account's editor on this shared device. Keys are namespaced by author but we sweep all.
      try {
        for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
          const key = window.localStorage.key(i);
          if (key?.startsWith(DRAFT_PREFIX)) window.localStorage.removeItem(key);
        }
      } catch {
        // Storage unavailable (private mode / quota) — nothing to purge; logout still completes.
      }
    }
  }, [clear]);

  return { login, childLogin, changePassword, logout };
}
